import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ArrowDownRight, X, ChevronRight, RefreshCw } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { API } from "../App";
import { toast } from "sonner";

/**
 * Compact tile showing the most recent performance alert.
 * Renders nothing when there's nothing to show, so it's safe on every dashboard.
 *
 * Props:
 *   onOpen      — fn(alertId) called when user clicks the chevron
 *   showRunNow  — bool (admins only). Shows a tiny "Check now" button.
 */
const PerformanceAlertTile = ({ onOpen, showRunNow = false }) => {
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchLatest = async () => {
    try {
      const res = await axios.get(`${API}/analytics/alerts/latest`);
      const data = res.data?.alert_id ? res.data : null;
      setAlert(data);
      // Restore dismissal state for this specific alert (sessionStorage scope).
      if (data?.alert_id) {
        const key = `perfalert_dismissed_${data.alert_id}`;
        setDismissed(sessionStorage.getItem(key) === "1");
      }
    } catch {
      setAlert(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (alert?.alert_id) {
      sessionStorage.setItem(`perfalert_dismissed_${alert.alert_id}`, "1");
    }
    setDismissed(true);
  };

  useEffect(() => { fetchLatest(); }, []);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/analytics/alerts/run-now`);
      if (res.data.alert_fired) {
        toast.success(`${res.data.findings.length} drop${res.data.findings.length > 1 ? "s" : ""} detected — recipients notified`);
        fetchLatest();
      } else {
        toast.success("All metrics healthy. Nothing to alert on.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run check");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !alert || dismissed) return null;

  const findings = alert.findings || [];
  if (findings.length === 0) return null;

  // Only show recent (last 7 days) so we don't surface stale alerts forever.
  const ranAt = new Date(alert.ran_at);
  const ageDays = (Date.now() - ranAt.getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays > 7) return null;

  const top = findings[0];

  return (
    <Card
      className="bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 border-rose-200"
      data-testid="performance-alert-tile"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-semibold text-rose-900 font-['Manrope']">
                  Performance alert
                </span>
                <span className="text-xs text-rose-700/80">
                  {findings.length} metric{findings.length > 1 ? "s" : ""} down · {ranAt.toLocaleDateString()}
                </span>
              </div>
              <div className="text-sm text-slate-700">
                <span className="font-semibold">{top.label}</span>{" "}
                <span className="text-rose-700 font-mono inline-flex items-center gap-0.5">
                  <ArrowDownRight className="w-3.5 h-3.5" /> {top.delta_pct}%
                </span>
                <span className="text-slate-500">
                  {" "}— {top.current}{top.unit} vs {top.prior}{top.unit} prior week
                </span>
              </div>
              {findings.length > 1 && (
                <div className="text-xs text-slate-500 mt-1">
                  + {findings.slice(1).map((f) => f.label).join(", ")}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {showRunNow && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRunNow}
                disabled={running}
                className="h-8 text-xs"
                data-testid="perf-alert-run-now"
                title="Force a fresh check now"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
              </Button>
            )}
            {onOpen && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpen(alert.alert_id)}
                className="h-8 text-xs text-rose-800 hover:bg-rose-100"
                data-testid="perf-alert-open"
              >
                Details <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
              data-testid="perf-alert-dismiss"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceAlertTile;
